// ============================================================
// AddConditionModal - Categorized condition picker with immunity
// awareness, duration presets, location picker, and custom
// penalty input. Follows SpawnDialog modal pattern.
// ============================================================
// The parts: ConditionBadge (a press), ConditionNumberPicker (the number
// row), ConditionLocationPicker, ConditionModifierForm. What each press
// does is `conditionPicker.ts`.
import { useState } from 'react';
import type { StackPolicy } from '../engine/stackPolicy';
import type { Target } from '../types';
import {
  getEligibleConditions,
  getLocationOptions,
  CATEGORY_ORDER,
  CATEGORY_LABELS,
} from '../engine/conditionEligibility';
import type { EligibleCondition, LocationOption } from '../engine/conditionEligibility';
import { badgeAction, groupByCategory, toggledCode, toggledIn, type BadgeAction } from './conditionPicker';
import { useConditionNumberPicker, type ConditionNumberPicker as NumberPickerState } from './useConditionNumberPicker';
import { ConditionBadge } from './ConditionBadge';
import { ConditionNumberPicker } from './ConditionNumberPicker';
import { ConditionModifierForm } from './ConditionModifierForm';
import useFocusTrap from '../hooks/useFocusTrap.js';
import useModalBackdrop from '../hooks/useModalBackdrop';
import common from '../styles/common.module.css';
import styles from '../styles/AddConditionModal.module.css';
import clsx from '../utils/clsx';

interface AddConditionModalProps {
  target: Target;
  /** `opts.stacking` is the picker's own ruling for this one (v8.367.2); absent = the session's. */
  onAdd: (conditionCode: string, opts?: { stacking?: StackPolicy }) => void;
  onClose: () => void;
  /**
   * No durations offered, and none written. LEVELS still are.
   *
   * Ground does not keep a timer. A condition a surface imposes lasts
   * exactly as long as you stand in it - `surfaceAt` derives it from
   * where you are and it is gone the instant you leave - so a round count
   * on it would be a second, competing answer to "when does this end",
   * and the wrong one: walk out on round one and a three-round timer
   * would still be running on a man standing in clean air.
   */
  timeless?: boolean;
  /**
   * Opened one level deeper inside another editor (the weapon bench's
   * effects, plan armoury): a drawer in place, never a modal on a modal.
   */
  inline?: boolean;
}

/** The picker itself: its header, the categories and the custom-penalty form. */
function ConditionPickerBody({ target, onAdd, onClose, timeless = false }: AddConditionModalProps) {
  const grouped = groupByCategory(getEligibleConditions(target as unknown as Record<string, unknown>));
  const bodyType = (target.bodyType as string) || 'humanoid';
  const picks = useConditionPicks(onAdd, timeless, getLocationOptions(bodyType as any));
  return (
    <>
      <div className={styles.header}>
        <h3 className={styles.title}>{timeless ? 'Add Condition - while they stand here' : 'Add Condition'}</h3>
        <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Close">
          &times;
        </button>
      </div>
      {CATEGORY_ORDER.map((cat) =>
        grouped[cat].length === 0 ? null : (
          <CategorySection key={cat} label={CATEGORY_LABELS[cat]} entries={grouped[cat]} picks={picks} />
        ),
      )}
      <ConditionModifierForm onAdd={onAdd} timeless={timeless} />
    </>
  );
}

function ConditionModal(props: AddConditionModalProps) {
  const trapRef = useFocusTrap(true);
  const backdrop = useModalBackdrop(props.onClose, trapRef);
  return (
    <div
      className={common.modal}
      onClick={backdrop.onBackdropClick}
      onKeyDown={(e) => e.key === 'Escape' && props.onClose()}
      role="presentation"
    >
      <div
        className={clsx(common.modalContent, styles.content)}
        ref={backdrop.boxRef}
        role="dialog"
        aria-modal="true"
        aria-label="Add condition"
      >
        <ConditionPickerBody {...props} />
      </div>
    </div>
  );
}

export default function AddConditionModal(props: AddConditionModalProps) {
  if (!props.inline) return <ConditionModal {...props} />;
  return (
    <section className={styles.drawer} aria-label="Add condition" data-testid="condition-drawer">
      <ConditionPickerBody {...props} />
    </section>
  );
}

/** The picker's state and verbs, shared by every category section. */
interface ConditionPicks {
  /** Immunities the GM unlocked (the lock icon). */
  overrides: Set<string>;
  press: (cond: EligibleCondition, shiftKey: boolean) => void;
  toggleLock: (code: string) => void;
  numbers: NumberPickerState;
  /** The location condition whose picker is open, or null. */
  locationFor: string | null;
  locationOptions: LocationOption[];
  pickLocation: (locName: string) => void;
}

/**
 * What a badge press, a lock and a location pick do. At most one picker is
 * open: opening the number picker closes the location picker and back.
 */
function useConditionPicks(
  onAdd: (code: string) => void,
  timeless: boolean,
  locationOptions: LocationOption[],
): ConditionPicks {
  const numbers = useConditionNumberPicker(onAdd);
  const [locationFor, setLocationFor] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Set<string>>(new Set());
  // What each badge action does; 'blocked' (an immune press) does nothing.
  const onAction: Record<Exclude<BadgeAction, 'blocked'>, (cond: EligibleCondition) => void> = {
    duration: (cond) => {
      setLocationFor(null);
      numbers.toggle(cond.code);
    },
    location: (cond) => {
      numbers.close();
      setLocationFor(toggledCode(locationFor, cond.code));
    },
    add: (cond) => onAdd(cond.code),
  };
  const press = (cond: EligibleCondition, shiftKey: boolean) => {
    const overridden = overrides.has(cond.code);
    const action = badgeAction(cond, { overridden, shiftKey, timeless, hasLocations: locationOptions.length > 0 });
    if (action !== 'blocked') onAction[action](cond);
  };
  const toggleLock = (code: string) => setOverrides((prev) => toggledIn(prev, code));
  const pickLocation = (locName: string) => {
    onAdd(`${locationFor}:${locName}`);
    setLocationFor(null);
  };
  return { overrides, press, toggleLock, numbers, locationFor, locationOptions, pickLocation };
}

interface CategorySectionProps {
  label: string;
  entries: EligibleCondition[];
  picks: ConditionPicks;
}

/** One category's badges, with the number or location picker inline below them when it is theirs. */
function CategorySection({ label, entries, picks }: CategorySectionProps) {
  const numberCond = entries.find((c) => c.code === picks.numbers.openFor);
  const locationFor = entries.some((c) => c.code === picks.locationFor) ? picks.locationFor : null;
  return (
    <div className={styles.categorySection}>
      <div className={styles.categoryHeader}>{label}</div>
      <div className={styles.badgeGrid}>
        {entries.map((cond) => (
          <ConditionBadge
            key={cond.code}
            cond={cond}
            overridden={picks.overrides.has(cond.code)}
            onPress={(shiftKey) => picks.press(cond, shiftKey)}
            onToggleLock={() => picks.toggleLock(cond.code)}
          />
        ))}
      </div>
      {numberCond && <ConditionNumberPicker cond={numberCond} picker={picks.numbers} />}
      {locationFor && <LocationPicker code={locationFor} options={picks.locationOptions} onPick={picks.pickLocation} />}
    </div>
  );
}

interface LocationPickerProps {
  code: string;
  options: LocationOption[];
  onPick: (locName: string) => void;
}

function LocationPicker({ code, options, onPick }: LocationPickerProps) {
  return (
    <div className={styles.subPanel} role="group" aria-label="Location picker">
      <div className={styles.subPanelLabel}>{code === 'Cr' ? 'Cripple' : 'Destroy'} which location?</div>
      <div className={styles.presetRow}>
        {options.map((loc) => (
          <button
            key={loc.id}
            className={styles.locationBtn}
            onClick={() => onPick(loc.label)}
            type="button"
            data-testid={`cond-loc-${loc.id}`}
          >
            {loc.label}
          </button>
        ))}
      </div>
    </div>
  );
}
